import React, { useState, useMemo } from 'react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';
import categoryIcons from '../../../data/categoryIcons.json';
 
const MediaLibrary = ({ onSelectImage }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  // Get all icons from JSON
  const allIcons = categoryIcons.icons;

  // Get unique categories
  const categories = useMemo(() => {
    const cats = ['All', ...new Set(allIcons.map(icon => icon.category))];
    return cats;
  }, [allIcons]);

  // Filter icons based on search and category
  const filteredIcons = useMemo(() => {
    let filtered = allIcons;

    // Filter by category
    if (selectedCategory !== 'All') {
      filtered = filtered.filter(icon => icon.category === selectedCategory);
    }

    // Filter by search term
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(icon => 
        icon.name.toLowerCase().includes(search) ||
        icon.category.toLowerCase().includes(search) ||
        icon.tags.some(tag => tag.toLowerCase().includes(search))
      );
    }

    return filtered;
  }, [allIcons, selectedCategory, searchTerm]);
  
  // Handle Image Selection
  const handleSelect = (src) => {
    if (onSelectImage) {
      console.log("Selected icon path:", src);
      onSelectImage(src);
    }
  };

  return (
    <div className="container-fluid p-4">
      <h2 className="text-center mb-4">Choose Category Icon</h2>
      
      {/* Search and Filter Section */}
      <div className="row mb-4">
        <div className="col-md-6 mb-3">
          <input
            type="text"
            className="form-control"
            placeholder="Search icons by name, category, or tags..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              borderRadius: '8px',
              padding: '10px 15px'
            }}
          />
        </div>
        <div className="col-md-6 mb-3">
          <select
            className="form-select"
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            style={{
              borderRadius: '8px',
              padding: '10px 15px'
            }}
          >
            {categories.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Icons Grid */}
      <div 
        className="row" 
        style={{ 
          maxHeight: '500px', 
          overflowY: 'auto',
          paddingRight: '10px',
          paddingTop: '50px',
          marginTop: '-50px'
        }}
      >
        {filteredIcons.map((icon) => (
          <div key={icon.id} className="col-lg-2 col-md-3 col-sm-4 col-6 mb-3" style={{ position: 'relative' }}>
            <div 
              className="card h-100 shadow-sm"
              style={{
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                border: '1px solid #dee2e6'
              }}
              onClick={() => handleSelect(icon.src)}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-5px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                e.currentTarget.style.borderColor = '#007bff';
                // Show tooltip
                const tooltip = e.currentTarget.querySelector('.icon-tooltip');
                if (tooltip) tooltip.style.opacity = '1';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.12)';
                e.currentTarget.style.borderColor = '#dee2e6';
                // Hide tooltip
                const tooltip = e.currentTarget.querySelector('.icon-tooltip');
                if (tooltip) tooltip.style.opacity = '0';
              }}
            >
              <LazyLoadImage
                src={icon.src}
                alt={icon.name}
                className="card-img-top p-3"
                style={{ 
                  height: "100px", 
                  width: "100%", 
                  objectFit: "contain",
                  imageRendering: "auto"
                }}
                effect="blur"
                placeholder={
                  <div className="d-flex align-items-center justify-content-center" style={{ height: "100px" }}>
                    <div className="spinner-border spinner-border-sm text-secondary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                }
                onError={(e) => {
                  console.error("Failed to load icon in gallery:", icon.src);
                  e.target.src = "/assets/img/icons/placeholder.svg";
                }}
              />
              {/* Tooltip on hover */}
              <div 
                className="icon-tooltip position-absolute"
                style={{
                  top: '-40px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  backgroundColor: '#000000',
                  color: '#ffffff',
                  padding: '6px 12px',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: '600',
                  whiteSpace: 'nowrap',
                  opacity: '0',
                  transition: 'opacity 0.3s ease',
                  pointerEvents: 'none',
                  zIndex: 1000,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                }}
              >
                {icon.name}
                {/* Arrow pointing down */}
                <div 
                  style={{
                    position: 'absolute',
                    bottom: '-6px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '0',
                    height: '0',
                    borderLeft: '6px solid transparent',
                    borderRight: '6px solid transparent',
                    borderTop: '6px solid #000000'
                  }}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {filteredIcons.length === 0 && (
        <div className="text-center py-5">
          <i className="fa fa-search fa-3x text-muted mb-3"></i>
          <p className="text-muted">No icons found matching your search criteria.</p>
          <button 
            className="btn btn-sm btn-outline-secondary"
            onClick={() => {
              setSearchTerm('');
              setSelectedCategory('All');
            }}
          >
            Clear Filters
          </button>
        </div>
      )}
      
      <div className="mt-3 text-center">
        <small className="text-muted">
          Showing {filteredIcons.length} of {allIcons.length} icons
        </small>
      </div>
    </div>
  );
};
export default MediaLibrary;

